import { ViewConfig } from '../../../models/view-config.interface';
import { CarrierToursPaginatedService } from '@app/carrier/services/carrier-tours-paginated.service';
import { ToursService } from 'api/services';
import {
    getCurrentOrganizationSiteKey,
    getIsTourPlanMode,
} from '@app/stores/appStore';
import { CarrierViewActionsService } from '../carrier-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { lastValueFrom } from 'rxjs';
import { ColumnCreator, ColumnValue } from '../column-creator';
import {
    TourData,
    DeliveryNoteData,
    TableRowData,
    hasKey,
    hasStatus,
    hasReadOnly,
    hasDateReceived,
    DeliveryNoteInTour,
} from '@app/shared/models/table-data-types';

export interface CarrierToursConfigDeps {
    carrierToursPaginatedService: CarrierToursPaginatedService;
    toursService: ToursService;
    actionsService: CarrierViewActionsService;
}

export class CarrierToursConfig {
    static create(deps: CarrierToursConfigDeps): ViewConfig {
        return {
            id: 'carrier-tours',
            name: 'Tours',
            translationKey: 'OUTGOING-GOODS.TOGGLER.TOURS',
            type: 'tours',
            userRole: 'carrier',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey = getCurrentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve([]);
                    }
                    return deps.carrierToursPaginatedService.loadCarrierToursPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        selectableCondition: (item: TableRowData) => {
                            const isOpen =
                                hasStatus(item) && item.status === 'open';
                            const isNotReadOnly =
                                !hasReadOnly(item) || !item.read_only;
                            return isOpen && isNotReadOnly;
                        },
                    }),
                    ColumnCreator.details(),
                    ColumnCreator.tourNumber(),
                    ColumnCreator.externalTourName(),
                    ColumnCreator.actions({
                        width: '250px',
                        configuration: {
                            type: 'bundle',
                            showDeliveryNotesCount: true,
                            showEdit: true,
                            showDelete: true,
                            showWalletInvite: true,
                            showDownload: true,
                            showViewPdf: true,

                            // Endpoints
                            editEndpoint: (item: TableRowData) =>
                                deps.actionsService.editTour(item as TourData),
                            deleteEndpoint: (item: TableRowData) =>
                                deps.actionsService.deleteTour(
                                    item as TourData
                                ),
                            walletInviteEndpoint: (item: TableRowData) =>
                                deps.actionsService.sendTourWalletInvite(
                                    item as TourData
                                ),
                            downloadEndpoint: (item: TableRowData) =>
                                deps.actionsService.downloadTour(
                                    item as TourData
                                ),
                            viewPdfEndpoint: (item: TableRowData) =>
                                deps.actionsService.viewTourPdf(
                                    item as TourData
                                ),

                            // Context
                            context: 'carrier',

                            // Visibility conditions
                            isEditVisible: (item: TableRowData) =>
                                hasStatus(item) &&
                                item.status === 'open' &&
                                !item.read_only,
                            isDeleteVisible: (item: TableRowData) =>
                                hasStatus(item) &&
                                item.status === 'open' &&
                                !item.read_only,
                            isWalletInviteVisible: (item: TableRowData) =>
                                hasStatus(item) &&
                                item.status === 'sent' &&
                                !item.read_only,
                            isDownloadVisible: (item: TableRowData) => true,
                            isViewPdfVisible: (item: TableRowData) => true,
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.dateShipped({
                        header: 'SPEDITEUR.TABLE.HEADERS.SENT-DATE',
                        filterable: true,
                    }),
                    ColumnCreator.status({
                        header: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                        showDiscrepanciesIcon: true,
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    expandable: true,
                },
                expandedRowConfig: {
                    expandableCondition: (item: TourData) =>
                        item.deliveryNoteKeys &&
                        item.deliveryNoteKeys.length > 0,
                    tableConfig: {
                        columns: [
                            ColumnCreator.actions({
                                width: '150px',
                                configuration: (
                                    row: TableRowData,
                                    value: ColumnValue,
                                    parentRow?: TableRowData
                                ) =>
                                    ({
                                        type: 'deliveryNote',
                                        showView: true,
                                        showViewPdf: true,
                                        showDownload: true,
                                        showDelete: true,

                                        // Endpoints
                                        viewEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.viewDeliveryNote(
                                                item as DeliveryNoteData,
                                                parentRow as TourData
                                            ),
                                        viewPdfEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.viewDeliveryNote(
                                                item as DeliveryNoteData,
                                                parentRow as TourData
                                            ),
                                        downloadEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.downloadDeliveryNote(
                                                item as DeliveryNoteData,
                                                parentRow as TourData
                                            ),
                                        deleteEndpoint: (
                                            item: TableRowData,
                                            bundleKey?: string,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.removeDeliveryNoteFromTour(
                                                item as DeliveryNoteData,
                                                parentRow as TourData
                                            ),

                                        // Configuration
                                        organizationSiteKey:
                                            parentRow?.orgSiteKey,
                                        tourKey:
                                            parentRow && hasKey(parentRow)
                                                ? parentRow._key
                                                : undefined,
                                        bundleStatus:
                                            parentRow && hasStatus(parentRow)
                                                ? parentRow.status
                                                : undefined,
                                        context: 'carrier',
                                        parentRowReadOnly: parentRow?.read_only,

                                        // Visibility conditions
                                        isEditVisible: () => false,
                                        isViewPdfVisible: () => true,
                                        isDeleteVisible: (
                                            item: TableRowData
                                        ) => {
                                            const tourIsOpen =
                                                parentRow &&
                                                hasStatus(parentRow)
                                                    ? parentRow.status ===
                                                      'open'
                                                    : false;
                                            const tourNotReadOnly =
                                                !parentRow?.read_only;
                                            return (
                                                tourIsOpen && tourNotReadOnly
                                            );
                                        },
                                    }) as TableActionsConfig,
                            }),
                            ColumnCreator.deliveryNoteNumber(),
                            ColumnCreator.referenceNumber({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NO',
                                orgType: 'carrier',
                                readOnly: (row, parentRow) => {
                                    const parentClosed =
                                        parentRow &&
                                        hasStatus(parentRow) &&
                                        parentRow.status === 'closed';
                                    const rowReadOnly =
                                        hasReadOnly(row) && row.read_only;
                                    const rowReceived =
                                        hasDateReceived(row) &&
                                        !!row.dateReceived;
                                    return (
                                        parentClosed ||
                                        rowReadOnly ||
                                        rowReceived
                                    );
                                },
                            }),
                            ColumnCreator.orderNumber(),
                            ColumnCreator.dateAnnounced(),
                            ColumnCreator.dateReceived(),
                            ColumnCreator.originalConsignor(),
                            ColumnCreator.originalConsignorGln(),
                            ColumnCreator.status({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE',
                            }),
                            ColumnCreator.discrepancies(),
                        ],
                        apiConfig: {
                            loadData: async (
                                parentRow: TourData,
                                organizationSiteKey: string,
                                customParams?: Record<string, unknown>
                            ) => {
                                const result = deps.toursService.getTour({
                                    tourKey: parentRow._key,
                                    organizationSiteKey: parentRow.carrierKey,
                                });
                                const tourDetails =
                                    result instanceof Promise
                                        ? await result
                                        : await lastValueFrom(result);

                                // Transform delivery notes to include required properties
                                return (tourDetails.deliveryNotes || []).map(
                                    (deliveryNote: DeliveryNoteInTour) => ({
                                        ...deliveryNote,
                                        orgSiteKey: parentRow.orgSiteKey,
                                        read_only:
                                            parentRow.status === 'closed' ||
                                            parentRow.read_only ||
                                            false,
                                        status:
                                            deliveryNote.dateReceived &&
                                            deliveryNote.dateReceived > 0
                                                ? 'closed'
                                                : tourDetails.status,
                                    })
                                );
                            },
                            organizationSiteKeyResolver: (row: TourData) =>
                                row.carrierKey,
                            customParams: (row: TourData) => ({
                                bundleKey: row._key,
                                bundleStatus: row.status,
                            }),
                        },
                        loadingMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.LOADING-DELIVERY-NOTES',
                        emptyMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.NO-DELIVERY-NOTES',
                        tableClass: 'expanded-delivery-notes-table',
                        maxHeight: '400px',
                    },
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'tourNumber',
                        label: 'SPEDITEUR.TABLE.HEADERS.TOUR-KEY',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.TOUR-KEY',
                    },
                    {
                        keyName: 'externalTourName',
                        label: 'SPEDITEUR.TABLE.HEADERS.TOUR-NAME',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.TOUR-NAME',
                    },
                    {
                        keyName: 'checkoutDate',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                    },
                ],
                showClosed: false,
                enableAdvancedFilters: false,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'name',
                    'status',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'tourNumber',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'planTour',
                        label: 'Plan Tour',
                        translationKey: 'SPEDITEUR.TABLE.ACTIONS.PLAN-TOUR',
                        icon: 'qr_code_scanner',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => false,
                        handler: async (item, selection) => {
                            await deps.actionsService.planTour(selection || []);
                        },
                    },
                    {
                        id: 'checkoutTour',
                        label: 'Checkout Tour',
                        translationKey: 'SPEDITEUR.TABLE.ACTIONS.CHECKOUT-TOUR',
                        svgIcon: 'folder-checkout',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 3,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => {
                            if (!selection || selection.length === 0)
                                return true;
                            return !selection.every(
                                tour =>
                                    tour.status !== 'completed' &&
                                    tour.deliveryNoteKeys &&
                                    tour.deliveryNoteKeys.length > 0
                            );
                        },
                        handler: async (item, selection) => {
                            await deps.actionsService.checkoutTourSelection(
                                selection || []
                            );
                        },
                    },
                ],
            },
            permissions: {
                canCreate: true,
                canUpdate: true,
                canDelete: true,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: () => !getIsTourPlanMode(),
            },
        };
    }
}
